import { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Alert, Space, Divider, Flex, Spin } from "antd";
import { ArrowLeftOutlined, CheckCircleFilled, ClockCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useTenantNavigation } from "../../hooks/useTenantNavigation.js";
import { showLoader } from "../../utils/showloader";
import VeoliaLogo from "../layout/assets/VeoliaLogo";
import { useTenant } from "../../context/TenantContext.jsx";
import {
  validateToken,
  sendPasswordResetOTP,
  verifyPasswordResetOTP,
  resetPassword,
} from "../../services/authServices.js";
import {
  DEFAULT_MIN_PASSWORD_LENGTH,
  buildPasswordRules,
  resolveMinPasswordLength,
} from "../../utils/passwordPolicy.js";
import { useTranslation } from 'react-i18next';

const MAX_RESENDS = 3;
const COOLDOWN_SECONDS = 120;

const CreatePassword = () => {
  const { t } = useTranslation();
  const navigate = useTenantNavigation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { tenantName, companyName, themeColor, companyLogo, fetchCompanyDetails } = useTenant();
  const [logoError, setLogoError] = useState(false);

  const [step, setStep] = useState(0); // 0 confirm email + send OTP, 1 verify OTP, 2 create password
  const [emailForm] = Form.useForm();
  const [otpForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [resendsLeft, setResendsLeft] = useState(MAX_RESENDS);
  // Minimum length is dictated by the backend, delivered on validate-token and
  // again on verify-otp.
  const [minPasswordLength, setMinPasswordLength] = useState(DEFAULT_MIN_PASSWORD_LENGTH);

  // Fetch company details on mount using tenant from URL
  useEffect(() => {
    // Extract tenant from URL path (e.g., /saltoris/create-password)
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const urlTenant = pathParts[0];

    if (urlTenant && urlTenant !== 'create-password') {
      fetchCompanyDetails(urlTenant);
    }
  }, [fetchCompanyDetails]);

  // Validate token on mount. The invitation token is what tells us which email
  // to prefill, so the first step cannot render until it comes back.
  useEffect(() => {
    const validateInvitationToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        setIsValidating(false);
        setErrorMessage("Invalid or Expire Link");
        return;
      }

      try {
        const resp = await validateToken(token);
        if (resp.success && resp.email) {
          setIsTokenValid(true);
          setUserEmail(resp.email);
          emailForm.setFieldsValue({ email: resp.email });
          setMinPasswordLength(resolveMinPasswordLength(resp.passwordLength));
        } else {
          setIsTokenValid(false);
          setErrorMessage(resp.message || "Invalid or expired token.");
        }
      } catch (err) {
        setIsTokenValid(false);
        setErrorMessage("Unable to validate token. Please try again.");
      } finally {
        setIsValidating(false);
      }
    };

    validateInvitationToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!cooldown) return;
    const timer = setInterval(() => setCooldown((c) => Math.max(c - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Driven by the form's onValuesChange rather than Form.useWatch: the password
  // form only mounts at step 2, and useWatch registers against `_init`, which is
  // still false on the render that first shows the form, so the watch never
  // attaches and the checklist would stay stuck on an empty value.
  const [passwordValue, setPasswordValue] = useState("");

  const passwordChecklist = useMemo(
    () =>
      buildPasswordRules(minPasswordLength, t).map((rule) => ({
        ...rule,
        ok: rule.test(passwordValue),
      })),
    [passwordValue, minPasswordLength, t]
  );

  const allRulesPass = passwordChecklist.every((r) => r.ok);

  const handleSendOtp = async () => {
    setSuccessMessage("");
    setErrorMessage("");

    if (!isTokenValid || !userEmail) {
      setErrorMessage("Invalid or Expire Link");
      return;
    }

    showLoader(true);
    try {
      // The email is fixed by the invitation, so it never comes from the form.
      const resp = await sendPasswordResetOTP(userEmail);
      if (resp.success && resp.otpToken) {
        setOtpToken(resp.otpToken);
        setSuccessMessage(resp.message || "We sent a verification code to your email.");
        setStep(1);
        setCooldown(COOLDOWN_SECONDS);
        setResendsLeft(MAX_RESENDS);
      } else {
        setErrorMessage(resp.message || "Unable to send OTP. Please try again.");
      }
    } catch (err) {
      setErrorMessage("Unable to send OTP. Please try again.");
    } finally {
      showLoader(false);
    }
  };

  const handleResend = async () => {
    if (cooldown || resendsLeft <= 0) return;
    setSuccessMessage("");
    setErrorMessage("");
    showLoader(true);
    try {
      const resp = await sendPasswordResetOTP(userEmail);
      if (resp.success) {
        setOtpToken(resp.otpToken);
        setSuccessMessage(resp.message || "OTP resent.");
        setResendsLeft((c) => Math.max(c - 1, 0));
        setCooldown(COOLDOWN_SECONDS);
      } else {
        setErrorMessage(resp.message || "Unable to resend now.");
      }
    } catch (err) {
      setErrorMessage("Unable to resend now.");
    } finally {
      showLoader(false);
    }
  };

  // `values` comes from the form's own submit, so the entered code is read
  // straight from what antd validated; getFieldValue covers a programmatic call.
  const handleVerifyOtp = async (values) => {
    setSuccessMessage("");
    setErrorMessage("");

    const enteredOtp = String(values?.otp ?? otpForm.getFieldValue("otp") ?? "").trim();
    if (!enteredOtp) {
      setErrorMessage("Enter the 6-digit code sent to your email.");
      return;
    }

    showLoader(true);
    try {
      const resp = await verifyPasswordResetOTP(userEmail, enteredOtp, otpToken);
      if (resp.success) {
        // verify-otp may omit passwordLength; keep the validate-token value then.
        if (resp.passwordLength != null) {
          setMinPasswordLength(resolveMinPasswordLength(resp.passwordLength));
        }
        setSuccessMessage(resp.message || "OTP verified. Set your password.");
        setStep(2);
      } else {
        setErrorMessage(resp.message || "Invalid OTP.");
      }
    } catch (err) {
      setErrorMessage("Invalid or expired OTP.");
    } finally {
      showLoader(false);
    }
  };

  // `values` comes from the form's own submit, so the typed passwords are read
  // straight from what antd validated; getFieldValue covers a programmatic call.
  const handleCreatePassword = async (values) => {
    setSuccessMessage("");
    setErrorMessage("");

    if (!userEmail || !otpToken) {
      setErrorMessage("Invalid or Expire Link");
      return;
    }

    // Never trimmed: leading/trailing spaces are legitimate password characters.
    const password = values?.password ?? passwordForm.getFieldValue("password") ?? "";
    const confirmPassword = values?.confirm ?? passwordForm.getFieldValue("confirm") ?? "";

    if (!password || !confirmPassword) {
      setErrorMessage("Enter and confirm your new password.");
      return;
    }

    showLoader(true);

    try {
      // The verified OTP is what authorises the change, carried as shareToken.
      const resp = await resetPassword(userEmail, password, confirmPassword, otpToken);
      if (resp.success) {
        setSuccessMessage(resp.message || "Password created successfully. You can now sign in.");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setErrorMessage(resp.message || "Unable to create password.");
      }
    } catch (err) {
      setErrorMessage("Unable to create password. Please try again.");
    } finally {
      showLoader(false);
    }
  };

  const getStepTitle = () => {
    if (isTokenValid && step === 0) return t('Confirm Your Email');
    if (isTokenValid && step === 1) return t('Verify OTP');
    return t('Create Your Password');
  };

  const getStepSubtitle = () => {
    if (isTokenValid && step === 0) {
      return t('Send a verification code to the email on your invitation.');
    }
    if (isTokenValid && step === 1) {
      return `${t('We sent a 6-digit code to')} ${userEmail}.`;
    }
    return `${t('Set up a secure password for your')} ${companyName} ${t('account.')}`;
  };

  const renderValidating = () => (
    <div className="text-center" style={{ padding: "32px 0" }}>
      <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      <p style={{ color: "rgba(0, 0, 0, 0.65)", marginTop: 16 }}>{t('Validating your invitation link...')}</p>
    </div>
  );

  const renderEmailStep = () => (
    <Form form={emailForm} layout="vertical" onFinish={handleSendOtp} className="auth-form">
      {/* Fixed by the invitation token - shown for confirmation, not for editing. */}
      <Form.Item label={t('Email')} name="email">
        <Input
          size="large"
          className="auth-input"
          style={{ borderColor: themeColor }}
          autoComplete="email"
          disabled
        />
      </Form.Item>

      <Button
        size="large"
        htmlType="submit"
        block
        className="auth-button"
        type="primary"
        style={{ backgroundColor: themeColor }}
      >
        {t('Send OTP')}
      </Button>

      <div className="text-center" style={{ marginTop: 16 }}>
        <span
          className="auth-link"
          onClick={() => navigate("/login")}
        >
          {t('Already have an account? Sign in')}
        </span>
      </div>
    </Form>
  );

  const renderOtpStep = () => (
    <Form form={otpForm} layout="vertical" onFinish={handleVerifyOtp} className="auth-form">
      <Form.Item
        label={t('Enter 6-digit code')}
        name="otp"
        rules={[
          { required: true, whitespace: true, message: "OTP is required" },
          { len: 6, message: "Enter the 6-digit code" },
          { pattern: /^\d+$/, message: "Digits only" },
        ]}
      >
        <Input
          inputMode="numeric"
          maxLength={6}
          placeholder="••••••"
          size="large"
          className="auth-input"
          style={{ borderColor: themeColor, textAlign: "center", letterSpacing: "0.35em", fontSize: 20 }}
          autoComplete="one-time-code"
        />
      </Form.Item>

      <Flex align="center" justify="center" gap={8} style={{ marginBottom: 16 }}>
        <ClockCircleOutlined style={{ color: "rgba(0, 0, 0, 0.45)" }} />
        <span style={{ color: "rgba(0, 0, 0, 0.65)", fontSize: 14 }}>
          {cooldown ? `Resend in ${cooldown}s` : "You can resend now"}
        </span>
        <Divider type="vertical" />
        <span style={{ color: "rgba(0, 0, 0, 0.65)", fontSize: 14 }}>
          {resendsLeft} resend{resendsLeft === 1 ? "" : "s"} left
        </span>
      </Flex>

      <Flex gap={12}>
        <Button
          size="large"
          className="auth-button"
          disabled={!!cooldown || resendsLeft <= 0}
          onClick={handleResend}
          style={{ flex: 1, borderColor: themeColor, color: themeColor }}
        >
          {t('Resend OTP')}
        </Button>
        <Button
          size="large"
          htmlType="submit"
          className="auth-button"
          type="primary"
          style={{ flex: 1, backgroundColor: themeColor }}
        >
          {t('Verify OTP')}
        </Button>
      </Flex>

      <div className="text-center" style={{ marginTop: 16 }}>
        <span
          className="auth-link"
          onClick={() => setStep(0)}
        >
          <ArrowLeftOutlined style={{ marginRight: 6 }} />
          {t('Back')}
        </span>
      </div>
    </Form>
  );

  const renderPasswordForm = () => (
    <Form
      form={passwordForm}
      layout="vertical"
      onFinish={handleCreatePassword}
      onValuesChange={(_, allValues) => setPasswordValue(allValues?.password || "")}
      className="auth-form"
    >
      {userEmail && (
        <div style={{ background: "#e6f4ff", borderRadius: 10, padding: 12, marginBottom: 16 }}>
          <span style={{ color: "rgba(0, 0, 0, 0.65)" }}>{t('Creating password for:')} </span>
          <span style={{ color: themeColor, fontWeight: 600 }}>{userEmail}</span>
        </div>
      )}

      <Form.Item
        label={t('New Password')}
        name="password"
        rules={[{ required: true, whitespace: true, message: "Password is required" }]}
      >
        <Input.Password
          placeholder={t('Enter new password')}
          size="large"
          className="auth-input"
          style={{ borderColor: themeColor }}
          autoComplete="new-password"
        />
      </Form.Item>

      <Form.Item
        label={t('Confirm Password')}
        name="confirm"
        dependencies={["password"]}
        rules={[
          { required: true, whitespace: true, message: "Confirm your password" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Passwords do not match"));
            },
          }),
        ]}
      >
        <Input.Password
          placeholder={t('Re-enter new password')}
          size="large"
          className="auth-input"
          style={{ borderColor: themeColor }}
          autoComplete="new-password"
        />
      </Form.Item>

      <div style={{ background: "#f5f5f5", borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ color: "rgba(0, 0, 0, 0.85)", fontWeight: 600, marginBottom: 8 }}>{t('Password requirements:')}</div>
        <Space direction="vertical" size={4}>
          {passwordChecklist.map((rule) => (
            <div key={rule.key} style={{ fontSize: 14, color: rule.ok ? "#52c41a" : "rgba(0, 0, 0, 0.45)" }}>
              {rule.ok ? <CheckCircleFilled style={{ marginRight: 8 }} /> : <span style={{ marginRight: 8 }}>•</span>}
              {rule.label}
            </div>
          ))}
        </Space>
      </div>

      <Button
        size="large"
        htmlType="submit"
        block
        className="auth-button"
        type="primary"
        disabled={!allRulesPass}
        style={{ backgroundColor: themeColor }}
      >
        {t('Create Password')}
      </Button>

      <div className="text-center" style={{ marginTop: 16 }}>
        <span
          className="auth-link"
          onClick={() => navigate("/login")}
        >
          {t('Already have an account? Sign in')}
        </span>
      </div>
    </Form>
  );

  const renderInvalidToken = () => (
    <div className="text-center">
      <p style={{ color: "rgba(0, 0, 0, 0.65)", marginBottom: 24 }}>
        {t('The password creation link is invalid or has expired. Please contact your administrator to request a new invitation.')}
      </p>
      <Button
        size="large"
        className="auth-button"
        type="primary"
        style={{ backgroundColor: themeColor }}
        onClick={() => navigate("/login")}
      >
        {t('Go to Login')}
      </Button>
    </div>
  );

  const renderContent = () => {
    if (isValidating) {
      return renderValidating();
    }
    if (!isTokenValid) {
      return renderInvalidToken();
    }
    if (step === 1) {
      return renderOtpStep();
    }
    if (step === 2) {
      return renderPasswordForm();
    }
    return renderEmailStep();
  };

  return (
    <Flex className="layoutStyle" justify="center">
      {/* Left side - same as login page */}
      <div className="leftSider" style={{ backgroundImage: "url('/assets/LoginBackground.png')", backgroundRepeat: "no-repeat" }}>
        <div className="top-logo">
          {companyLogo && !logoError ? (
            <img
              src={companyLogo}
              alt={t('Company Logo')}
              style={{ maxWidth: "176px", maxHeight: "60px" }}
              onError={() => setLogoError(true)}
            />
          ) : (
            <VeoliaLogo type="small" />
          )}
        </div>
        <div className="middle-section">
          <img src="/assets/LoginImg1.svg" style={{ width: "11rem", height: "11rem" }} alt="" />
          <img src="/assets/LoginImg2.svg" className="center-image" style={{ width: "13.375rem", height: "12.25rem" }} alt="" />
          <img src="/assets/LoginImg3.svg" style={{ width: "16rem", height: "10rem" }} alt="" />
        </div>
        <div className="bottom-section">
          <div className="login-title" style={{ color: themeColor }}>{t('Welcome to')} {companyName}!</div>
          <div className="login-subtitle">{t('Create your password to get started with your account.')}</div>
        </div>
      </div>

      {/* Right side - form area */}
      <div className="rightSider">
        <div className="auth-panel">
          <div className="auth-header">
            <h1 className="auth-title">{getStepTitle()}</h1>
            <p className="auth-subtitle">{getStepSubtitle()}</p>
          </div>

          {successMessage && (
            <Alert type="success" message={successMessage} showIcon />
          )}
          {errorMessage && !isValidating && (
            <Alert type="error" message={errorMessage} showIcon />
          )}

          <div>
            {renderContent()}
          </div>
        </div>
      </div>
    </Flex>
  );
};

export default CreatePassword;
